import { ActionNode, ControlNode } from "@/shared/ui/composites";
import { Application } from "pixi.js";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type {
	DragEvent as ReactDragEvent,
	PointerEvent as ReactPointerEvent,
	WheelEvent as ReactWheelEvent,
} from "react";
import { useWorkflowCanvasElements } from "./useWorkflowCanvasElements";
import type { WorkflowCanvasElementsController } from "./useWorkflowCanvasElements";
import type {
	AddWorkflowCanvasElementInput,
	WorkflowCanvasControlData,
	WorkflowCanvasElement,
	WorkflowCanvasPosition,
} from "./workflow-canvas.types";

export interface WorkflowCanvasProps {
	className?: string;
	onEditElement?: (element: WorkflowCanvasElement) => void;
	controller?: Pick<
		WorkflowCanvasElementsController,
		| "elements"
		| "edges"
		| "selectedElementId"
		| "addElement"
		| "moveElement"
		| "removeElement"
		| "addEdge"
		| "selectElement"
		| "clearSelection"
	>;
}

export interface WorkflowCanvasHandle {
	fitToContent: () => void;
}

interface DragState {
	elementId: string;
	offsetX: number;
	offsetY: number;
}

interface PanState {
	lastClientX: number;
	lastClientY: number;
}

interface ConnectingState {
	fromElementId: string;
	fromPort: "bottom" | "left" | "right";
	edgeLabel?: string;
	pointerWorld: WorkflowCanvasPosition;
}

interface ViewportOffset {
	x: number;
	y: number;
}

interface CanvasSize {
	width: number;
	height: number;
}

interface NodeSize {
	width: number;
	height: number;
}

const NODE_WIDTH = 280;
const NODE_HEIGHT = 112;
const PORT_SIZE = 14;
const OUTPUT_PORT_BOTTOM = -PORT_SIZE / 2;
const CULLING_BUFFER = 600;
const WORKFLOW_DND_TYPE = "application/x-workflow-node";
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;
const ZOOM_FACTOR = 0.004;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const isElementVisible = (
	element: WorkflowCanvasElement,
	viewport: ViewportOffset,
	scale: number,
	canvasSize: CanvasSize,
) => {
	const x = element.position.x * scale + viewport.x;
	const y = element.position.y * scale + viewport.y;

	return (
		x + NODE_WIDTH * scale >= -CULLING_BUFFER &&
		x <= canvasSize.width + CULLING_BUFFER &&
		y + NODE_HEIGHT * scale >= -CULLING_BUFFER &&
		y <= canvasSize.height + CULLING_BUFFER
	);
};

const getCurvePath = (from: WorkflowCanvasPosition, to: WorkflowCanvasPosition) => {
	const distanceY = Math.abs(to.y - from.y);
	const controlOffset = Math.max(50, Math.min(180, distanceY * 0.5));

	return `M ${from.x} ${from.y} C ${from.x} ${from.y + controlOffset}, ${to.x} ${to.y - controlOffset}, ${to.x} ${to.y}`;
};

const getEdgeLabelPosition = (
	from: WorkflowCanvasPosition,
	to: WorkflowCanvasPosition,
): WorkflowCanvasPosition => {
	return {
		x: (from.x + to.x) / 2,
		y: (from.y + to.y) / 2 - 12,
	};
};

interface OutputPortConfig {
	port: "bottom" | "left" | "right";
	label?: string;
}

const getControlOutputPorts = (data: WorkflowCanvasControlData, t: TFunction): OutputPortConfig[] => {
	if (data.moduleType === "condition") {
		return [
			{ port: "left", label: t("workflow.portTrue") },
			{ port: "right", label: t("workflow.portFalse") },
		];
	}

	if (data.moduleType === "loop") {
		return [
			{ port: "left", label: t("workflow.portLoop") },
			{ port: "right", label: t("workflow.portExit") },
		];
	}

	return [{ port: "bottom" }];
};

const renderElement = (
	element: WorkflowCanvasElement,
	isSelected: boolean,
	onDelete: () => void,
	onEdit: () => void,
) => {
	if (element.kind === "action") {
		return (
			<ActionNode
				name={element.data.name}
				description={element.data.description}
				gyulAmount={element.data.gyulAmount}
				selected={isSelected}
				onDelete={onDelete}
				onEdit={onEdit}
			/>
		);
	}

	return (
		<ControlNode
			name={element.data.name}
			description={element.data.description}
			selected={isSelected}
			onDelete={onDelete}
			onEdit={onEdit}
		/>
	);
};

export const WorkflowCanvas = forwardRef<WorkflowCanvasHandle, WorkflowCanvasProps>(({ className, controller, onEditElement }, ref) => {
	const { t } = useTranslation();
	const rootRef = useRef<HTMLDivElement | null>(null);
	const pixiHostRef = useRef<HTMLDivElement | null>(null);
	const dragStateRef = useRef<DragState | null>(null);
	const panStateRef = useRef<PanState | null>(null);

	const internalController = useWorkflowCanvasElements();
	const activeController = controller ?? internalController;

	const {
		elements,
		edges,
		selectedElementId,
		addElement,
		moveElement,
		removeElement,
		addEdge,
		selectElement,
		clearSelection,
	} = activeController;
	const [viewportOffset, setViewportOffset] = useState<ViewportOffset>({ x: 0, y: 0 });
	const [viewportScale, setViewportScale] = useState(1);
	const [canvasSize, setCanvasSize] = useState<CanvasSize>({ width: 0, height: 0 });
	const [poolSize, setPoolSize] = useState(0);
	const [isPanning, setIsPanning] = useState(false);
	const [connectingState, setConnectingState] = useState<ConnectingState | null>(null);
	const [nodeSizes, setNodeSizes] = useState<Record<string, NodeSize>>({});

	const elementById = useMemo(() => {
		return new Map(elements.map((element) => [element.id, element]));
	}, [elements]);

	const getNodeSize = (elementId: string): NodeSize => {
		return nodeSizes[elementId] ?? { width: NODE_WIDTH, height: NODE_HEIGHT };
	};

	const getInputPortPosition = (element: WorkflowCanvasElement): WorkflowCanvasPosition => {
		const nodeSize = getNodeSize(element.id);

		return {
			x: element.position.x + nodeSize.width / 2,
			y: element.position.y,
		};
	};

	const getOutputPortPosition = (
		element: WorkflowCanvasElement,
		port: "bottom" | "left" | "right" = "bottom",
	): WorkflowCanvasPosition => {
		const nodeSize = getNodeSize(element.id);

		if (port === "left") {
			return {
				x: element.position.x,
				y: element.position.y + nodeSize.height / 2,
			};
		}

		if (port === "right") {
			return {
				x: element.position.x + nodeSize.width,
				y: element.position.y + nodeSize.height / 2,
			};
		}

		return {
			x: element.position.x + nodeSize.width / 2,
			y: element.position.y + nodeSize.height - OUTPUT_PORT_BOTTOM - PORT_SIZE / 2,
		};
	};

	const handleNodeMeasure = (node: HTMLDivElement | null, elementId: string) => {
		if (!node) return;

		const measuredWidth = node.offsetWidth;
		const measuredHeight = node.offsetHeight;

		setNodeSizes((current) => {
			const previous = current[elementId];
			if (previous && previous.width === measuredWidth && previous.height === measuredHeight) {
				return current;
			}

			return {
				...current,
				[elementId]: {
					width: measuredWidth,
					height: measuredHeight,
				},
			};
		});
	};

	const visibleElements = useMemo(
		() =>
			elements.filter((element) =>
				isElementVisible(element, viewportOffset, viewportScale, canvasSize),
			),
		[elements, viewportOffset, viewportScale, canvasSize],
	);

	useEffect(() => {
		if (visibleElements.length > poolSize) {
			setPoolSize(visibleElements.length);
		}
	}, [visibleElements.length, poolSize]);

	const pooledElements = useMemo(() => {
		return Array.from({ length: poolSize }, (_, index) => visibleElements[index] ?? null);
	}, [poolSize, visibleElements]);

	useEffect(() => {
		const root = rootRef.current;
		if (!root) return;

		const updateSize = () => {
			setCanvasSize({ width: root.clientWidth, height: root.clientHeight });
		};

		updateSize();

		const observer = new ResizeObserver(updateSize);
		observer.observe(root);

		return () => {
			observer.disconnect();
		};
	}, []);

	useEffect(() => {
		const container = rootRef.current;
		const pixiHost = pixiHostRef.current;
		if (!container || !pixiHost) return;

		const app = new Application();
		let destroyRequested = false;
		let isInitialized = false;
		let isDestroyed = false;

		const destroyApp = () => {
			if (!isInitialized || isDestroyed) {
				return;
			}

			isDestroyed = true;
			app.destroy(true, { children: true, texture: true, textureSource: true });
		};

		void (async () => {
			try {
				await app.init({
					antialias: true,
					autoDensity: true,
					background: "var(--bg-weak)",
					resizeTo: container,
				});
			} catch (error) {
				console.error("Failed to initialize Pixi application", error);
				return;
			}

			isInitialized = true;

			if (destroyRequested) {
				destroyApp();
				return;
			}

			pixiHost.replaceChildren(app.canvas);
		})();

		return () => {
			destroyRequested = true;
			destroyApp();
		};
	}, []);

	const toWorldPosition = (
		clientX: number,
		clientY: number,
		rootRect: DOMRect,
		viewport: ViewportOffset,
		scale: number,
	) => {
		return {
			x: (clientX - rootRect.left - viewport.x) / scale,
			y: (clientY - rootRect.top - viewport.y) / scale,
		};
	};

	const toCanvasPosition = (
		worldPosition: WorkflowCanvasPosition,
		viewport: ViewportOffset,
		scale: number,
	) => {
		return {
			x: worldPosition.x * scale + viewport.x,
			y: worldPosition.y * scale + viewport.y,
		};
	};

	const clearInteractions = () => {
		dragStateRef.current = null;
		panStateRef.current = null;
		setIsPanning(false);
		setConnectingState(null);
	};

	const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>, elementId: string) => {
		if (connectingState) {
			return;
		}

		const root = rootRef.current;
		if (!root) return;

		const rootRect = root.getBoundingClientRect();
		const targetElement = elementById.get(elementId);
		if (!targetElement) {
			return;
		}

		const pointerWorldPosition = toWorldPosition(
			event.clientX,
			event.clientY,
			rootRect,
			viewportOffset,
			viewportScale,
		);

		dragStateRef.current = {
			elementId,
			offsetX: pointerWorldPosition.x - targetElement.position.x,
			offsetY: pointerWorldPosition.y - targetElement.position.y,
		};
		selectElement(elementId);

		event.currentTarget.setPointerCapture(event.pointerId);
		event.stopPropagation();
	};

	const startConnection = (
		event: ReactPointerEvent<HTMLButtonElement>,
		fromElementId: string,
		fromPort: "bottom" | "left" | "right",
		edgeLabel?: string,
	) => {
		const root = rootRef.current;
		if (!root) return;

		const rootRect = root.getBoundingClientRect();
		const pointerWorldPosition = toWorldPosition(
			event.clientX,
			event.clientY,
			rootRect,
			viewportOffset,
			viewportScale,
		);

		selectElement(fromElementId);
		setConnectingState({
			fromElementId,
			fromPort,
			edgeLabel,
			pointerWorld: pointerWorldPosition,
		});

		event.stopPropagation();
	};

	const completeConnection = (toElementId: string) => {
		if (!connectingState || connectingState.fromElementId === toElementId) {
			setConnectingState(null);
			return;
		}

		addEdge({
			fromElementId: connectingState.fromElementId,
			toElementId,
			sourcePort: connectingState.fromPort,
			label: connectingState.edgeLabel,
		});

		setConnectingState(null);
	};

	const handleNodePointerUp = (event: ReactPointerEvent<HTMLDivElement>, elementId: string) => {
		if (!connectingState) {
			return;
		}

		completeConnection(elementId);
		event.stopPropagation();
	};

	const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
		if (connectingState) {
			const root = rootRef.current;
			if (!root) return;

			const rootRect = root.getBoundingClientRect();
			const pointerWorldPosition = toWorldPosition(
				event.clientX,
				event.clientY,
				rootRect,
				viewportOffset,
				viewportScale,
			);

			setConnectingState((current) =>
				current
					? {
							...current,
							pointerWorld: pointerWorldPosition,
						}
					: null,
			);
			return;
		}

		const dragState = dragStateRef.current;
		if (dragState) {
			const root = rootRef.current;
			if (!root) return;

			const rootRect = root.getBoundingClientRect();
			const pointerWorldPosition = toWorldPosition(
				event.clientX,
				event.clientY,
				rootRect,
				viewportOffset,
				viewportScale,
			);

			moveElement(dragState.elementId, {
				x: pointerWorldPosition.x - dragState.offsetX,
				y: pointerWorldPosition.y - dragState.offsetY,
			});
			return;
		}

		const panState = panStateRef.current;
		if (!panState) return;

		const deltaX = event.clientX - panState.lastClientX;
		const deltaY = event.clientY - panState.lastClientY;

		panStateRef.current = {
			lastClientX: event.clientX,
			lastClientY: event.clientY,
		};

		setViewportOffset((current) => ({
			x: current.x + deltaX,
			y: current.y + deltaY,
		}));
	};

	const handleCanvasPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
		if (event.target !== event.currentTarget) {
			return;
		}

		if (connectingState) {
			setConnectingState(null);
			return;
		}

		dragStateRef.current = null;
		clearSelection();

		panStateRef.current = {
			lastClientX: event.clientX,
			lastClientY: event.clientY,
		};
		setIsPanning(true);

		event.currentTarget.setPointerCapture(event.pointerId);
	};

	const handleWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
		event.preventDefault();

		if (event.ctrlKey || event.metaKey) {
			const root = rootRef.current;
			if (!root) return;

			const rect = root.getBoundingClientRect();
			const cursorX = event.clientX - rect.left;
			const cursorY = event.clientY - rect.top;
			const zoomMultiplier = Math.exp(-event.deltaY * ZOOM_FACTOR);
			const nextScale = clamp(viewportScale * zoomMultiplier, MIN_ZOOM, MAX_ZOOM);

			if (nextScale === viewportScale) {
				return;
			}

			const worldX = (cursorX - viewportOffset.x) / viewportScale;
			const worldY = (cursorY - viewportOffset.y) / viewportScale;

			setViewportScale(nextScale);
			setViewportOffset({
				x: cursorX - worldX * nextScale,
				y: cursorY - worldY * nextScale,
			});
			return;
		}

		setViewportOffset((current) => ({
			x: current.x - event.deltaX,
			y: current.y - event.deltaY,
		}));
	};

	const handleDragOver = (event: ReactDragEvent<HTMLDivElement>) => {
		if (event.dataTransfer.types.includes(WORKFLOW_DND_TYPE)) {
			event.preventDefault();
		}
	};

	const handleDrop = (event: ReactDragEvent<HTMLDivElement>) => {
		const serialized = event.dataTransfer.getData(WORKFLOW_DND_TYPE);
		if (!serialized) {
			return;
		}

		let payload: AddWorkflowCanvasElementInput | null = null;
		try {
			payload = JSON.parse(serialized) as AddWorkflowCanvasElementInput;
		} catch (error) {
			console.error("Invalid workflow drop payload", error);
			return;
		}

		if (!payload) {
			return;
		}

		const root = rootRef.current;
		if (!root) {
			return;
		}

		const rect = root.getBoundingClientRect();
		const worldPosition = toWorldPosition(
			event.clientX,
			event.clientY,
			rect,
			viewportOffset,
			viewportScale,
		);

		addElement({
			...payload,
			position: worldPosition,
		});

		event.preventDefault();
	};

	const handlePointerUp = () => {
		if (connectingState) {
			setConnectingState(null);
		}

		clearInteractions();
	};

	const handleDelete = (id: string) => {
		removeElement(id);
	};

	const handleEdit = (id: string) => {
		selectElement(id);
		const element = elementById.get(id);
		if (element && onEditElement) {
			onEditElement(element);
		}
	};

	const fitToContent = useCallback(() => {
		if (elements.length === 0 || canvasSize.width === 0 || canvasSize.height === 0) return;

		let minX = Infinity;
		let minY = Infinity;
		let maxX = -Infinity;
		let maxY = -Infinity;

		for (const el of elements) {
			minX = Math.min(minX, el.position.x);
			minY = Math.min(minY, el.position.y);
			maxX = Math.max(maxX, el.position.x + NODE_WIDTH);
			maxY = Math.max(maxY, el.position.y + NODE_HEIGHT);
		}

		const contentWidth = maxX - minX;
		const contentHeight = maxY - minY;
		if (contentWidth <= 0 || contentHeight <= 0) return;

		const FIT_PADDING = 80;
		const scaleX = (canvasSize.width - FIT_PADDING * 2) / contentWidth;
		const scaleY = (canvasSize.height - FIT_PADDING * 2) / contentHeight;
		const scale = clamp(Math.min(scaleX, scaleY, 1), MIN_ZOOM, MAX_ZOOM);

		setViewportScale(scale);
		setViewportOffset({
			x: (canvasSize.width - contentWidth * scale) / 2 - minX * scale,
			y: 100 - minY * scale,
		});
	}, [elements, canvasSize]);

	useImperativeHandle(ref, () => ({ fitToContent }), [fitToContent]);

	return (
		<div ref={rootRef} className={`absolute w-full h-full overflow-hidden ${className ?? ""}`}>
			<div ref={pixiHostRef} className="absolute inset-0" />
			<div
				className={`absolute inset-0 z-10 select-none touch-none ${isPanning ? "cursor-grabbing" : "cursor-grab"}`}
				onPointerDown={handleCanvasPointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUp}
				onPointerCancel={handlePointerUp}
				onPointerLeave={handlePointerUp}
				onWheel={handleWheel}
				onDragOver={handleDragOver}
				onDrop={handleDrop}
			>
				<svg
					className="absolute inset-0 pointer-events-none overflow-visible"
					width="100%"
					height="100%"
				>
					<defs>
						<pattern
							id="dotGrid"
							width={20 * viewportScale}
							height={20 * viewportScale}
							patternUnits="userSpaceOnUse"
							x={viewportOffset.x}
							y={viewportOffset.y}
						>
							<circle
								cx={1}
								cy={1}
								r={1}
								fill="var(--outline)"
								opacity={0.4}
							/>
						</pattern>
					</defs>
					<rect width="100%" height="100%" fill="url(#dotGrid)" />
					{edges.map((edge) => {
						const fromElement = elementById.get(edge.fromElementId);
						const toElement = elementById.get(edge.toElementId);

						if (!fromElement || !toElement) {
							return null;
						}

						const fromCanvas = toCanvasPosition(
							getOutputPortPosition(fromElement, edge.sourcePort ?? "bottom"),
							viewportOffset,
							viewportScale,
						);
						const toCanvas = toCanvasPosition(
							getInputPortPosition(toElement),
							viewportOffset,
							viewportScale,
						);
						const labelPosition = getEdgeLabelPosition(fromCanvas, toCanvas);

						return (
							<g key={edge.id}>
								<path
									d={getCurvePath(fromCanvas, toCanvas)}
									stroke="var(--outline)"
									strokeWidth={2}
									fill="none"
								/>
								{edge.description ? (
									<text
										x={labelPosition.x}
										y={labelPosition.y}
										fill="var(--ct-weak)"
										fontSize={12}
										textAnchor="middle"
									>
										{edge.description}
									</text>
								) : null}
							</g>
						);
					})}
					{connectingState &&
						(() => {
							const fromElement = elementById.get(connectingState.fromElementId);
							if (!fromElement) {
								return null;
							}

							const fromCanvas = toCanvasPosition(
								getOutputPortPosition(fromElement, connectingState.fromPort),
								viewportOffset,
								viewportScale,
							);
							const pointerCanvas = toCanvasPosition(
								connectingState.pointerWorld,
								viewportOffset,
								viewportScale,
							);

							return (
								<path
									d={getCurvePath(fromCanvas, pointerCanvas)}
									stroke="var(--outline)"
									strokeWidth={2}
									fill="none"
									strokeDasharray="6 4"
								/>
							);
						})()}
				</svg>

				{pooledElements.map((element, poolIndex) =>
					(() => {
						const nodeSize = element
							? getNodeSize(element.id)
							: { width: NODE_WIDTH, height: NODE_HEIGHT };
						const canvasPosition = element
							? toCanvasPosition(element.position, viewportOffset, viewportScale)
							: { x: 0, y: 0 };

						return (
							<div
								key={poolIndex}
								ref={element ? (node) => handleNodeMeasure(node, element.id) : undefined}
								className="absolute cursor-grab active:cursor-grabbing"
								style={
									element
										? {
												transform: `translate(${canvasPosition.x}px, ${canvasPosition.y}px) scale(${viewportScale})`,
												transformOrigin: "top left",
												display: "block",
											}
										: { display: "none" }
								}
								onPointerDown={
									element ? (event) => handlePointerDown(event, element.id) : undefined
								}
								onPointerUp={
									element ? (event) => handleNodePointerUp(event, element.id) : undefined
								}
							>
								{element ? (
									<>
										{renderElement(
											element,
											selectedElementId === element.id,
											() => handleDelete(element.id),
											() => handleEdit(element.id),
										)}

										{selectedElementId === element.id ? (
											<>
												{(element.kind === "control"
													? getControlOutputPorts(element.data, t)
													: [{ port: "bottom" as const }]
												).map((outputPort) => {
													const commonStyle = {
														width: PORT_SIZE,
														height: PORT_SIZE,
														background: "var(--bg-weak)",
														borderColor: "var(--ct-weak)",
													};

													if (outputPort.port === "left") {
														return (
															<button
																key={`${element.id}-port-left`}
																type="button"
																className="absolute rounded-full border"
																style={{
																	...commonStyle,
																	left: -PORT_SIZE / 2,
																	top: nodeSize.height / 2 - PORT_SIZE / 2,
																}}
																onPointerDown={(event) =>
																	startConnection(event, element.id, "left", outputPort.label)
																}
															/>
														);
													}

													if (outputPort.port === "right") {
														return (
															<button
																key={`${element.id}-port-right`}
																type="button"
																className="absolute rounded-full border"
																style={{
																	...commonStyle,
																	right: -PORT_SIZE / 2,
																	top: nodeSize.height / 2 - PORT_SIZE / 2,
																}}
																onPointerDown={(event) =>
																	startConnection(event, element.id, "right", outputPort.label)
																}
															/>
														);
													}

													return (
														<button
															key={`${element.id}-port-bottom`}
															type="button"
															className="absolute rounded-full border"
															style={{
																...commonStyle,
																bottom: OUTPUT_PORT_BOTTOM,
																left: nodeSize.width / 2 - PORT_SIZE / 2,
															}}
															onPointerDown={(event) =>
																startConnection(event, element.id, "bottom", outputPort.label)
															}
														/>
													);
												})}
											</>
										) : null}
									</>
								) : null}
							</div>
						);
					})(),
				)}
			</div>
		</div>
	);
});
